import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  CostAdjustment as CostAdjustment,
  CostAdjustment_DistributionMethod as DistributionMethod,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import {
  AllocationStrategies,
  FractionalMoney,
  Money,
} from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"

import { CostBreakdownItem } from "features/pos/transactions/components/CostBreakdownTooltip"

// DistributableLine is a product line a CostAdjustment can be split across.
// quantity and cost must be in the same unit of measure.
export interface DistributableLine {
  id: PBUUID
  quantity: Decimal
  cost: FractionalMoney
}

// DistributedCost is this line's share of a shipment-level CostAdjustment.
// The name is display-only; the backend only persists unitFee, keyed by
// costAdjustmentId (see OrderShipment.Product.distributed_costs).
export interface DistributedCost extends CostBreakdownItem {
  costAdjustmentId: PBUUID | undefined
}

// isCostAdjustmentsEqual compares two CostAdjustment arrays ignoring their ids
// because a new, unsaved CostAdjustment has no id yet
export function isCostAdjustmentsEqual(
  a: CostAdjustment[],
  b: CostAdjustment[]
) {
  return (
    a.length === b.length &&
    a.every((line, i) =>
      CostAdjustment.equals(
        { ...line, id: undefined },
        { ...b[i], id: undefined }
      )
    )
  )
}

// calculateDistributedCostsByProductId splits each distributed
// CostAdjustment's amount across lines, merged per line id.
export function calculateDistributedCostsByProductId(
  costAdjustments: CostAdjustment[],
  lines: DistributableLine[]
): Record<string, DistributedCost[]> {
  const distributedCostsByProductId: Record<string, DistributedCost[]> = {}

  for (const costAdjustment of costAdjustments) {
    const amount = Money.fromPB(costAdjustment.amount)
    const sharesByProductId = (() => {
      switch (costAdjustment.distributionMethod) {
        case DistributionMethod.BY_QUANTITY:
          return distributeByQuantity(
            costAdjustment.name,
            costAdjustment.id,
            amount,
            lines
          )
        case DistributionMethod.BY_SUBTOTAL:
          return distributeBySubtotal(
            costAdjustment.name,
            costAdjustment.id,
            amount,
            lines
          )
        default:
          return {}
      }
    })()

    for (const productId in sharesByProductId) {
      distributedCostsByProductId[productId] ??= []
      distributedCostsByProductId[productId].push(sharesByProductId[productId])
    }
  }

  return distributedCostsByProductId
}

function distributeByQuantity(
  name: string,
  costAdjustmentId: PBUUID | undefined,
  amount: Money,
  lines: DistributableLine[]
): Record<string, DistributedCost> {
  const quantities = lines.map((line) => line.quantity)
  const totalQuantity = quantities.reduce(
    (sum, quantity) => sum.add(quantity),
    Decimal.zero()
  )
  if (totalQuantity.isZero()) {
    return {}
  }

  const lineShares = amount.allocate(
    quantities,
    AllocationStrategies.LargestRemainder
  )
  return sharesByLineId(name, costAdjustmentId, lines, lineShares)
}

function distributeBySubtotal(
  name: string,
  costAdjustmentId: PBUUID | undefined,
  amount: Money,
  lines: DistributableLine[]
): Record<string, DistributedCost> {
  // Subtotals stay unrounded so the weights match the server's allocation.
  const subtotals = lines.map((line) => line.cost.mult(line.quantity))
  const totalSubtotal = subtotals.reduce(
    (sum, subtotal) => sum.add(subtotal),
    FractionalMoney.zero()
  )
  if (totalSubtotal.eq(FractionalMoney.zero())) {
    return {}
  }

  const subtotalWeights = subtotals.map((subtotal) => subtotal.centsAsDecimal())
  const lineShares = amount.allocate(
    subtotalWeights,
    AllocationStrategies.LargestRemainder
  )
  return sharesByLineId(name, costAdjustmentId, lines, lineShares)
}

// sharesByLineId turns each line's allocated share into a per-unit fee.
// Lines without quantity get no share.
function sharesByLineId(
  name: string,
  costAdjustmentId: PBUUID | undefined,
  lines: DistributableLine[],
  lineShares: Money[]
): Record<string, DistributedCost> {
  const shares: Record<string, DistributedCost> = {}
  lines.forEach((line, i) => {
    if (line.quantity.isZero()) return
    shares[UUID.fromPB(line.id).toString()] = {
      name,
      unitFee: FractionalMoney.fromMoney(lineShares[i]).div(line.quantity),
      costAdjustmentId,
    }
  })
  return shares
}
